import { useState, useEffect } from "react"

/**
 * Responsive state interface
 */
interface ResponsiveState {
  /** True if screen width is less than 1400px */
  isSmallScreen: boolean
  /** True if screen width is less than 576px */
  isMobileScreen: boolean
}

/**
 * Custom hook for responsive screen size detection and management.
 *
 * Monitors window resize events and provides boolean flags for different screen sizes.
 * Uses predefined breakpoints: 1400px for small screens, 576px for mobile screens.
 *
 * @function useResponsive
 * @returns Object containing responsive state flags
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isSmallScreen, isMobileScreen } = useResponsive()
 *
 *   return (
 *     <div>
 *       {isSmallScreen ? 'Small Screen UI' : 'Large Screen UI'}
 *       {isMobileScreen && 'Mobile Specific Content'}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Using in conditional rendering
 * const { isSmallScreen } = useResponsive()
 * const sidebarWidth = isSmallScreen ? '85%' : '256px'
 * ```
 */
export const useResponsive = (): ResponsiveState => {
  /** Small screen state (< 1400px) */
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    (): boolean => window.innerWidth < 1400
  )

  /** Mobile screen state (< 576px) */
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(
    (): boolean => window.innerWidth < 576
  )

  useEffect(() => {
    /**
     * Handles window resize events and updates screen size states
     */
    const handleResize = (): void => {
      const width: number = window.innerWidth
      const newIsSmallScreen: boolean = width < 1400
      const newIsMobileScreen: boolean = width < 576

      setIsSmallScreen(newIsSmallScreen)
      setIsMobileScreen(newIsMobileScreen)
    }

    // Add resize event listener
    window.addEventListener("resize", handleResize)

    // Cleanup function to remove event listener
    return (): void => window.removeEventListener("resize", handleResize)
  }, [])

  return { isSmallScreen, isMobileScreen }
}
